import React, { useState, useEffect, useCallback } from "react";
import "./App.css";

const correctSound = new Audio("/sounds/correct.mp3");
const wrongSound = new Audio("/sounds/wrong.mp3");
const winSound = new Audio("/sounds/win.mp3");

// -------- SUDOKU ENGINE --------

const emptyGrid = () => Array(9).fill(0).map(() => Array(9).fill(0));

const isSafe = (grid, r, c, n) => {
  for (let i = 0; i < 9; i++)
    if (grid[r][i] === n || grid[i][c] === n) return false;

  const br = Math.floor(r / 3) * 3;
  const bc = Math.floor(c / 3) * 3;

  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      if (grid[br + i][bc + j] === n) return false;

  return true;
};

const solve = grid => {
  for (let r = 0; r < 9; r++)
    for (let c = 0; c < 9; c++)
      if (grid[r][c] === 0) {
        for (let n = 1; n <= 9; n++) {
          if (isSafe(grid, r, c, n)) {
            grid[r][c] = n;
            if (solve(grid)) return true;
            grid[r][c] = 0;
          }
        }
        return false;
      }
  return true;
};

const generateSudoku = difficulty => {
  let grid = emptyGrid();

  solve(grid);

  const solution = grid.map(r => [...r]);

  const remove =
    difficulty === "easy" ? 35 :
    difficulty === "medium" ? 45 : 55;

  let count = remove;

  while (count) {
    let r = Math.floor(Math.random() * 9);
    let c = Math.floor(Math.random() * 9);
    if (grid[r][c] !== 0) {
      grid[r][c] = 0;
      count--;
    }
  }

  return { board: grid, solution };
};

// -------- APP --------

export default function App() {
  const [board, setBoard] = useState([]);
  const [solution, setSolution] = useState([]);
  const [initialBoard, setInitialBoard] = useState([]);
  const [difficulty, setDifficulty] = useState("easy");
  const [seconds, setSeconds] = useState(0);
  const [score, setScore] = useState(0);
  const [win, setWin] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [notes, setNotes] = useState({});
  const [mistakes, setMistakes] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const newGame = useCallback(() => {
    const { board, solution } = generateSudoku(difficulty);
    setBoard(board);
    setSolution(solution);
    setInitialBoard(board.map(r => [...r]));
    setSeconds(0);
    setScore(0);
    setWin(false);
    setNotes({});
    setMistakes(0);
    setHintsUsed(0);
    setIsPaused(false);
  }, [difficulty]);

  useEffect(() => newGame(), [newGame]);

  useEffect(() => {
    if (!isPaused && !win) {
      const t = setInterval(() => setSeconds(s => s + 1), 1000);
      return () => clearInterval(t);
    }
  }, [isPaused, win]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const s = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const addNote = (r, c, n) => {
    const k = `${r}-${c}`;
    setNotes(p => ({ ...p, [k]: p[k] ? p[k] + n : n }));
  };

  const handleChange = (r, c, v) => {
    if (initialBoard[r][c] !== 0) return; // Prevent editing initial values
    if (!/^[1-9]?$/.test(v)) return;

    const copy = board.map(row => [...row]);
    const num = v === "" ? 0 : Number(v);

    copy[r][c] = num;
    setBoard(copy);

    if (num !== 0) {
      if (num === solution[r][c]) {
        setScore(s => s + 10);
        correctSound.play().catch(() => {});
      } else {
        setScore(s => Math.max(0, s - 5));
        setMistakes(m => m + 1);
        wrongSound.play().catch(() => {});
      }
    }

    checkWin(copy);
  };

  const checkWin = g => {
    for (let i = 0; i < 9; i++)
      for (let j = 0; j < 9; j++)
        if (g[i][j] !== solution[i][j]) return;

    winSound.play().catch(() => {});
    setWin(true);
  };

  const getHint = () => {
    if (win) return;
    
    const emptyCells = [];
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        if (board[i][j] === 0) {
          emptyCells.push({ r: i, c: j });
        }
      }
    }

    if (emptyCells.length === 0) return;

    const randomCell = emptyCells[Math.floor(Math.random() * emptyCells.length)];
    const copy = board.map(row => [...row]);
    copy[randomCell.r][randomCell.c] = solution[randomCell.r][randomCell.c];
    setBoard(copy);
    setHintsUsed(h => h + 1);
    setScore(s => Math.max(0, s - 15));
    checkWin(copy);
  };

  const clearBoard = () => {
    setBoard(initialBoard.map(r => [...r]));
    setNotes({});
    setScore(0);
    setMistakes(0);
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const getCompletionPercentage = () => {
    if (!board || board.length === 0) return 0;
    let filled = 0;
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        if (board[i] && board[i][j] !== 0) filled++;
      }
    }
    return Math.round((filled / 81) * 100);
  };

  return (
    <div className="container">
      <header className="header">
        <h1 className="title">Sudoku Master</h1>
        <p className="subtitle">Challenge your mind with classic logic puzzles</p>
      </header>

      <div className="controls-panel">
        <div className="control-group">
          <label htmlFor="difficulty">Difficulty:</label>
          <select 
            id="difficulty"
            value={difficulty} 
            onChange={e => setDifficulty(e.target.value)}
            className="select-difficulty"
          >
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-label">Time:</span>
            <span className="stat-value">{formatTime(seconds)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Score:</span>
            <span className="stat-value">{score}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Mistakes:</span>
            <span className="stat-value">{mistakes}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Hints:</span>
            <span className="stat-value">{hintsUsed}</span>
          </div>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${getCompletionPercentage()}%` }}>
          <span className="progress-text">{getCompletionPercentage()}% Complete</span>
        </div>
      </div>

      {isPaused && (
        <div className="pause-overlay">
          <div className="pause-message">Game Paused</div>
        </div>
      )}

      <div className={`game-board ${isPaused ? 'blurred' : ''}`}>
        <div className="grid">
          {board.map((row, r) =>
            row.map((cell, c) => {
              const correct = cell === solution[r][c];
              const isInitial = initialBoard[r][c] !== 0;
              const isActive = activeCell && activeCell.r === r && activeCell.c === c;
              const isHighlighted = activeCell && (activeCell.r === r || activeCell.c === c);
              const isSameValue = cell !== 0 && activeCell && board[activeCell.r][activeCell.c] === cell;

              return (
                <div key={`${r}-${c}`} className="cellWrapper">
                  <input
                    value={cell || ""}
                    onFocus={() => setActiveCell({ r, c })}
                    onBlur={() => setActiveCell(null)}
                    onContextMenu={e => {
                      e.preventDefault();
                      if (!isInitial) {
                        const n = prompt("Enter note (1-9):");
                        if (n >= 1 && n <= 9) addNote(r, c, n);
                      }
                    }}
                    onChange={e => handleChange(r, c, e.target.value)}
                    disabled={isPaused || win}
                    readOnly={isInitial}
                    className={`cell 
                      ${isActive ? "active" : ""} 
                      ${isHighlighted ? "highlight" : ""} 
                      ${isSameValue ? "same-value" : ""}
                      ${isInitial ? "initial" : ""}
                      ${cell !== 0 && !isInitial ? (correct ? "correct" : "incorrect") : ""}`}
                  />
                  {notes[`${r}-${c}`] && (
                    <div className="note">{notes[`${r}-${c}`]}</div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {win && (
        <div className="win-message">
          <h2>Congratulations!</h2>
          <p>You solved the puzzle in {formatTime(seconds)}</p>
          <p>Final Score: {score} points</p>
        </div>
      )}

      <div className="action-buttons">
        <button onClick={newGame} className="btn btn-primary">New Game</button>
        <button onClick={togglePause} className="btn btn-secondary" disabled={win}>
          {isPaused ? "Resume" : "Pause"}
        </button>
        <button onClick={getHint} className="btn btn-hint" disabled={win || isPaused}>
          Get Hint (-15 pts)
        </button>
        <button onClick={clearBoard} className="btn btn-danger">Clear Board</button>
        <button onClick={() => setShowStats(!showStats)} className="btn btn-info">
          {showStats ? "Hide Stats" : "Show Stats"}
        </button>
      </div>

      {showStats && (
        <div className="stats-panel">
          <h3>Game Statistics</h3>
          <div className="stats-grid">
            <div className="stats-item">
              <span className="stats-label">Completion:</span>
              <span className="stats-value">{getCompletionPercentage()}%</span>
            </div>
            <div className="stats-item">
              <span className="stats-label">Accuracy:</span>
              <span className="stats-value">
                {mistakes === 0 ? "100%" : `${Math.max(0, 100 - (mistakes * 5))}%`}
              </span>
            </div>
            <div className="stats-item">
              <span className="stats-label">Average per Cell:</span>
              <span className="stats-value">
                {Math.round(seconds / Math.max(1, getCompletionPercentage() * 0.81))}s
              </span>
            </div>
          </div>
        </div>
      )}

      <footer className="footer">
        <p>Right-click on any cell to add notes</p>
        <p>Initial values are locked and shown in bold</p>
      </footer>
    </div>
  );
}